import { icon, node } from "~/util/dom";
import ReportFormField from "./index";
import { MIN_ZOOM, MAX_WORLD_ZOOM } from "~/report/const";
import { ICommonOpts } from "~/report/interfaces";
import { _ } from "~/i18n";
import { store } from "~/state/store";

interface IOpts extends ICommonOpts {
	container: HTMLElement;
	value: {
		longitude: number;
		latitude: number;
		isKnownAddress: boolean;
	};
	processMapData: (data: any) => any;
	onChange: (address: string, addressData: any) => void;
}

export default class ReportFormFieldMap extends ReportFormField {
	#opts: IOpts;

	#map: SMap;

	#marker: SMap.Marker;

	#markerLayer: SMap.Layer.Marker;

	#mapControl: HTMLButtonElement;

	#photoLayer: SMap.Layer;

	#hybridLayer: SMap.Layer;

	#turistLayer: SMap.Layer;

	#turistLayerControl: HTMLButtonElement;

	#photoLayerControl: HTMLButtonElement;

	#syncControl: SMap.Control.Sync;

	#zoomControl: SMap.Control.Zoom;

	#mouseControl: SMap.Control.Mouse;

	#infoNode: HTMLDivElement;

	#infoControl: HTMLButtonElement;

	#isOpen: boolean;

	#isMarkerShown: boolean;

	#activeLayerName: string;

	constructor(opts: IOpts) {
		super(opts);

		const { longitude, latitude, zoom } = store.getState().map;

		this.#opts = {
			container: null,
			processMapData: () => { /**/ },
			onChange: () => { /**/ },
			...opts,
			value: {
				longitude,
				latitude,
				isKnownAddress: false,
				...opts.value,
			},
		};

		this.#build(zoom);
	}

	#open() {
		super.container.classList.add("open");
		this.#infoNode.classList.add("open");
		this.#map.addControl(this.#syncControl);
		this.#map.addControl(this.#mouseControl);
		this.#map.addControl(this.#zoomControl, { right: "20px", top: "20px" });
		this.#isOpen = true;
		this.#opts.container.dispatchEvent(new Event("resize-content"));
	}

	handleEvent(evnt) {
		if (evnt.type === "click") {
			if (this.#mapControl.contains(evnt.target)) {
				this.#open();
			} else if (this.#turistLayerControl.contains(evnt.target)) {
				this.#setLayer("turist");
			} else if (this.#photoLayerControl.contains(evnt.target)) {
				this.#setLayer("photo");
			} else if (this.#infoControl.contains(evnt.target)) {
				this.#infoNode.classList.remove("open");
			}
		}
	}

	isOpen() {
		return this.#isOpen;
	}

	#build(zoom: number) {
		const { value: { longitude, latitude, isKnownAddress } } = this.#opts;
		const layersControls = node("div", { className: "button-group map-control open" });

		this.#mapControl = node("button", { className: "map-control" });

		const gpsIcon = icon("report-gps");

		const text = node("span", { className: "map-control-text", innerText: _("report.updateOnMap") });

		this.#mapControl.append(gpsIcon, text);

		this.#turistLayerControl = node("button", { className: "turisticka svg-icon", title: _("gallery.map.control"), innerHTML: `<span>${_("gallery.map.control")}</span>` });
		this.#photoLayerControl = node("button", { className: "icon ophoto", title: _("mapsetSwitch.ophoto"), innerHTML: `<span>${_("mapsetSwitch.ophoto")}</span>` });

		this.#turistLayerControl.firstChild.before(icon("winter-map"));
		layersControls.append(this.#turistLayerControl, this.#photoLayerControl);

		this.#infoNode = node("div", { className: "map-info" });
		const infoText = node("span", { className: "map-info-text", innerText: _("report.mapNote") });

		this.#infoControl = node("button", { className: "map-info-control" });
		const closeIcon = icon("close");

		this.#infoControl.append(closeIcon);

		this.#infoNode.append(infoText, this.#infoControl);

		super.container.append(this.#mapControl, layersControls, this.#infoNode);
		super.container.addEventListener("click", this, false);

		const center = SMap.Coords.fromWGS84(longitude, latitude);

		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		this.#map = new SMap(super.container, center, Math.min(zoom, MAX_WORLD_ZOOM), {
			minZoom: MIN_ZOOM,
			maxZoom: MAX_WORLD_ZOOM,
			ophotoDate: true,
		});
		this.#markerLayer = new SMap.Layer.Marker();

		this.#map.getSignals().addListener(undefined, "map-click", signal => {
			if (this.isOpen()) {
				const coords = SMap.Coords.fromEvent(signal.data.event, this.#map);
				const label = coords.toWGS84(2).reverse().join(", ");

				const place = {
					source: "coor",
					id: `${coords.x},${coords.y}`,
					coords,
					label,
				};

				this.changeMarkedPlace(coords.x, coords.y);

				const placeData = typeof this.#opts.processMapData === "function"
					? this.#opts.processMapData(place)
					: place;

				this.#opts.onChange(`${coords.y},${coords.x}`, placeData);
			}
		});

		const coords = SMap.Coords.fromWGS84(longitude, latitude);

		this.#marker = new SMap.Marker(coords, null, {
			title: "marked place",
			anchor: { left: 14, top: 14 },
			url: "/img/marker/selected.png",
		});

		this.#markerLayer.addMarker(this.#marker);

		this.#photoLayer = this.#map.addDefaultLayer(SMap.DEF_OPHOTO);
		this.#hybridLayer = this.#map.addDefaultLayer(SMap.DEF_HYBRID);
		this.#turistLayer = this.#map.addDefaultLayer(SMap.DEF_TURIST);

		// odstranime logo
		const logo = this.#map.getControls().find(control => control instanceof SMap.Control.MapyLogo);
		const copyright = this.#map.getControls().find(control => control instanceof SMap.Control.Copyright);

		if (logo) {
			this.#map.removeControl(logo);
			logo.$destructor();
		}

		if (copyright) {
			this.#map.removeControl(copyright);
			copyright.$destructor();
		}

		this.#syncControl = new SMap.Control.Sync();
		this.#zoomControl = new SMap.Control.Zoom(null, { showZoomMenu: false, titles: [SMap._("map.zoomIn"), SMap._("map.zoomOut")] });
		this.#mouseControl = new SMap.Control.Mouse(SMap.MOUSE_WHEEL | SMap.MOUSE_PAN | SMap.MOUSE_ZOOM);

		this.#map.addLayer(this.#markerLayer);

		if (isKnownAddress) {
			this.#showMarker();
		}

		this.#setLayer("turist");

		setTimeout(() => {
			this.#map.syncPort();
		}, 1);
	}

	#setLayer(name: string) {
		switch (name) {
			case "turist":
				if (this.#activeLayerName !== "turist") {
					this.#activeLayerName = "turist";
					this.#turistLayer.enable();
					this.#photoLayer.disable();
					this.#hybridLayer.disable();
					this.#turistLayerControl.classList.add("active");
					this.#photoLayerControl.classList.remove("active");
				}
				break;
			case "photo":
				if (this.#activeLayerName !== "photo") {
					this.#activeLayerName = "photo";
					this.#turistLayer.disable();
					this.#photoLayer.enable();
					this.#hybridLayer.enable();
					this.#photoLayerControl.classList.add("active");
					this.#turistLayerControl.classList.remove("active");
				}
				break;
			default:
		}
	}

	changeMarkedPlace(lon: number, lat: number, zoom?: number) {
		const coords = SMap.Coords.fromWGS84(lon, lat);

		this.#map.setCenterZoom(coords, zoom || this.#map.getZoom());
		this.#marker.setCoords(coords);
		this.#showMarker();
	}

	#showMarker() {
		if (!this.#isMarkerShown) {
			this.#isMarkerShown = true;
			this.#markerLayer.enable();
		}
	}

	hideMarker() {
		if (this.#isMarkerShown) {
			this.#isMarkerShown = false;
			this.#markerLayer.disable();
		}
	}
}
